"use client";

/**
 * Inline source badge for the rail showing citation type, value, and label.
 *
 * Extracted from `compass-intakeflow.jsx:253-270` (`.if-badge`).
 */

import { useState } from "react";
import { ProvenanceDot } from "../data-display/ProvenanceDot";
import { ChevD, ChevR } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";
import { PROV_META } from "../lib/provenance";

export interface IntakeSourceBadgeProps {
  citation: {
    value?: string;
    label: string;
    type: ProvenanceType;
    by?: string;
  };
}

export function IntakeSourceBadge({ citation: c }: IntakeSourceBadgeProps) {
  const [open, setOpen] = useState(false);
  const et = c.type === "vault" ? "catalog" : c.type;
  const meta = PROV_META[et] ?? PROV_META.estimated;

  return (
    <div
      className={[
        "border border-(--border-subtle) rounded-(--radius-field) bg-(--bg-white)",
        open ? "pb-2" : "",
      ].join(" ")}
    >
      <button
        type="button"
        className={[
          "flex items-center gap-[11px] w-full text-left px-3 py-2.5",
          "bg-transparent border-0 cursor-pointer",
          "font-[family-name:var(--font-body)]",
        ].join(" ")}
        onClick={() => setOpen((o) => !o)}
      >
        <ProvenanceDot type={c.type} />
        <span className="flex-1 min-w-0 flex flex-col">
          <span className="text-[13px] font-semibold text-(--fg-primary) truncate">
            {c.label}
          </span>
          <span className="text-[11px] text-(--fg-muted) mt-px">
            {c.type === "provided" ? `Provided · ${c.by ?? "you"}` : meta.label}
          </span>
        </span>
        {c.value && (
          <span className="text-[14px] font-bold text-(--fg-primary) shrink-0">
            {c.value}
          </span>
        )}
        <span className="shrink-0 text-(--fg-muted)">
          {open ? <ChevD size={15} /> : <ChevR size={15} />}
        </span>
      </button>
      {open && (
        <div className="px-3 text-[12px] text-(--fg-muted)">
          Source detail panel would render here.
        </div>
      )}
    </div>
  );
}
